import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../context/UserContext';
import { apiFetch, apiJson } from '../../utils/apiClient';
import { useMessages } from './MessagesContext';
import ReviewDialog from '../ReviewDialog/ReviewDialog';
import './MessagesShared.css';

const dayKeyFromUnix = (unix) => {
    const d = new Date(Number(unix) * 1000);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const formatChatDateLabel = (unix) => {
    const date = new Date(Number(unix) * 1000);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
        a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();

    if (sameDay(date, today)) return 'Today';
    if (sameDay(date, yesterday)) return 'Yesterday';
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatChatTime = (unix) => {
    const date = new Date(Number(unix) * 1000);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
    });
};

const buildThreadItems = (messages) => {
    const items = [];
    let lastDayKey = null;
    for (const message of messages) {
        const key = dayKeyFromUnix(message.sentUnix);
        if (key !== lastDayKey) {
            items.push({
                type: 'date',
                key: `date-${key}`,
                label: formatChatDateLabel(message.sentUnix),
            });
            lastDayKey = key;
        }
        items.push({ type: 'message', key: message.messageId, message });
    }
    return items;
};

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;

const renderMessageText = (text) => {
    if (!text) return null;
    const parts = text.split(URL_PATTERN);
    return parts.map((part, index) => {
        if (/^https?:\/\//.test(part)) {
            return (
                <a
                    key={`url-${index}`}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {part}
                </a>
            );
        }
        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
};

const conversationField = (conversation, ...keys) => {
    if (!conversation) return undefined;
    for (const key of keys) {
        const value = conversation[key];
        if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
};

const normalizeReview = (data) => {
    if (!data || typeof data !== 'object') return null;
    const reviewId = Number(data.reviewId ?? data.ReviewId);
    const rating = Number(data.rating ?? data.Rating ?? 0);
    const reviewText = data.reviewText ?? data.ReviewText ?? '';
    const hasReviewId = Number.isFinite(reviewId) && reviewId > 0;
    if (!hasReviewId && !rating && !reviewText) return null;
    return {
        reviewId: hasReviewId ? reviewId : Date.now(),
        rating: rating || 5,
        reviewText,
    };
};

const mergeServerWithLocal = (serverList, localMessages) => {
    const extras = localMessages.filter((local) => {
        if (!local.pending && !local.failed) return false;
        return !serverList.some((server) => (
            Number(server.senderUserId) === Number(local.senderUserId)
            && server.messageText === local.messageText
            && Math.abs(Number(server.sentUnix) - Number(local.sentUnix)) < 120
        ));
    });
    return [...serverList, ...extras];
};

const ConversationThread = ({
    conversationId,
    initialConversation,
    draft = null,
    variant = 'page',
    onBack,
}) => {
    const { user } = useContext(UserContext);
    const { fetchConversations, startConversation } = useMessages();
    const isDraft = Boolean(draft) && !conversationId;
    const [conversation, setConversation] = useState(
        initialConversation || (isDraft ? {
            otherUserId: draft.otherUserId,
            otherUsername: draft.otherUsername,
            marketplaceItemId: draft.marketplaceItemId,
            collectionItemId: draft.collectionItemId,
            itemTitle: draft.itemTitle,
        } : null)
    );
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(!isDraft);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [showReview, setShowReview] = useState(false);
    const [submittingReview, setSubmittingReview] = useState(false);
    const [existingReview, setExistingReview] = useState(null);
    const bottomRef = useRef(null);
    const inFlightRef = useRef(false);
    const reviewFetchRef = useRef(0);
    const numericId = Number(conversationId);

    const fetchExistingReview = async (reviewedUserId) => {
        if (!user || !numericId || !reviewedUserId) return null;
        const fetchId = reviewFetchRef.current + 1;
        reviewFetchRef.current = fetchId;
        try {
            const data = await apiJson('/MarketplaceReviews/GetConversationReview', {
                body: {
                    userId: String(user.userId),
                    reviewedUserId,
                    conversationId: numericId,
                },
            });
            if (fetchId !== reviewFetchRef.current) return null;
            const normalized = normalizeReview(data);
            if (normalized) {
                setExistingReview(normalized);
            }
            return normalized;
        } catch (error) {
            console.error(error);
            if (fetchId === reviewFetchRef.current) {
                setExistingReview((prev) => prev ?? null);
            }
            return null;
        }
    };

    const fetchConversation = async () => {
        if (!user || !numericId) return;
        try {
            const res = await apiFetch('/MarketplaceChat/GetConversation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: String(user.userId),
                    conversationId: numericId,
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setConversation((prev) => {
                    if (!prev) return data;
                    const dataMarketId = Number(conversationField(data, 'marketplaceItemId', 'MarketplaceItemId')) || 0;
                    const prevMarketId = Number(conversationField(prev, 'marketplaceItemId', 'MarketplaceItemId')) || 0;
                    const dataCollectionId = Number(conversationField(data, 'collectionItemId', 'CollectionItemId')) || 0;
                    const prevCollectionId = Number(conversationField(prev, 'collectionItemId', 'CollectionItemId')) || 0;
                    return {
                        ...prev,
                        ...data,
                        marketplaceItemId: dataMarketId || prevMarketId || null,
                        collectionItemId: dataCollectionId || prevCollectionId || null,
                        itemTitle: conversationField(data, 'itemTitle', 'ItemTitle')
                            || conversationField(prev, 'itemTitle', 'ItemTitle')
                            || '',
                    };
                });
                const otherUserId = Number(conversationField(data, 'otherUserId', 'OtherUserId'));
                if (otherUserId > 0) {
                    fetchExistingReview(otherUserId);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchMessages = async (markRead = false, { initial = false } = {}) => {
        if (!user || !numericId) return;
        if (!markRead && typeof document !== 'undefined' && document.hidden) return;
        if (!markRead && inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const data = await apiJson('/MarketplaceChat/GetMessages', {
                body: {
                    userId: String(user.userId),
                    conversationId: numericId,
                    pageSize: 50,
                },
            });
            const list = Array.isArray(data)
                ? data
                : (data?.messages || data?.Messages || []);
            setMessages((prev) => (initial ? list : mergeServerWithLocal(list, prev)));
            if (markRead) {
                await apiFetch('/MarketplaceChat/MarkConversationRead', {
                    body: {
                        userId: String(user.userId),
                        conversationId: numericId,
                    },
                });
                fetchConversations();
            }
        } catch (error) {
            console.error(error);
        } finally {
            inFlightRef.current = false;
            if (initial) setLoadingMessages(false);
        }
    };

    useEffect(() => {
        if (!initialConversation) return;
        setConversation((prev) => {
            const initialId = Number(conversationField(initialConversation, 'conversationId', 'ConversationId'));
            const prevId = Number(conversationField(prev, 'conversationId', 'ConversationId'));
            if (!prev || prevId !== initialId) return initialConversation;

            // Keep richer GetConversation fields if the inbox summary is thinner.
            const listMarketId = Number(conversationField(initialConversation, 'marketplaceItemId', 'MarketplaceItemId')) || 0;
            const prevMarketId = Number(conversationField(prev, 'marketplaceItemId', 'MarketplaceItemId')) || 0;
            const listCollectionId = Number(conversationField(initialConversation, 'collectionItemId', 'CollectionItemId')) || 0;
            const prevCollectionId = Number(conversationField(prev, 'collectionItemId', 'CollectionItemId')) || 0;
            return {
                ...prev,
                ...initialConversation,
                marketplaceItemId: listMarketId || prevMarketId || null,
                collectionItemId: listCollectionId || prevCollectionId || null,
                itemTitle: conversationField(initialConversation, 'itemTitle', 'ItemTitle')
                    || conversationField(prev, 'itemTitle', 'ItemTitle')
                    || '',
                otherUsername: conversationField(initialConversation, 'otherUsername', 'OtherUsername')
                    || conversationField(prev, 'otherUsername', 'OtherUsername'),
            };
        });
    }, [initialConversation, numericId]);

    useEffect(() => {
        if (isDraft) {
            setLoadingMessages(false);
            setMessages([]);
            setConversation({
                otherUserId: draft.otherUserId,
                otherUsername: draft.otherUsername,
                marketplaceItemId: draft.marketplaceItemId,
                collectionItemId: draft.collectionItemId,
                itemTitle: draft.itemTitle,
            });
            return undefined;
        }
        if (!user || !numericId) return undefined;
        setLoadingMessages(true);
        setMessages([]);
        setText('');
        setExistingReview(null);
        reviewFetchRef.current += 1;
        fetchConversation();
        fetchMessages(true, { initial: true });

        let intervalId = null;
        const clearPoll = () => {
            if (intervalId != null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };
        const startPoll = () => {
            clearPoll();
            if (typeof document !== 'undefined' && document.hidden) return;
            intervalId = setInterval(() => fetchMessages(false), 12000);
        };
        const onVisibility = () => {
            if (document.hidden) {
                clearPoll();
            } else {
                fetchMessages(false);
                startPoll();
            }
        };

        startPoll();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearPoll();
            document.removeEventListener('visibilitychange', onVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, numericId, isDraft, draft?.otherUserId]);

    useEffect(() => {
        if (!user || !numericId || !conversation) return;
        const otherUserId = Number(conversationField(conversation, 'otherUserId', 'OtherUserId'));
        if (otherUserId > 0) {
            fetchExistingReview(otherUserId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, numericId, conversation]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: variant === 'drawer' ? 'auto' : 'smooth' });
    }, [messages, variant]);

    const handleSend = async (event) => {
        event.preventDefault();
        const messageText = text.trim();
        if (!messageText || !user || sending) return;

        // Draft: create the conversation only when the first message is sent.
        if (isDraft) {
            setSending(true);
            try {
                await startConversation({
                    otherUserId: draft.otherUserId,
                    marketplaceItemId: draft.marketplaceItemId,
                    collectionItemId: draft.collectionItemId,
                    initialMessage: messageText,
                });
                setText('');
            } catch (error) {
                console.error(error);
            } finally {
                setSending(false);
            }
            return;
        }

        const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const optimistic = {
            messageId: tempId,
            conversationId: numericId,
            senderUserId: user.userId,
            messageText,
            sentUnix: Math.floor(Date.now() / 1000),
            pending: true,
        };

        setSending(true);
        setText('');
        setMessages((prev) => [...prev, optimistic]);

        try {
            const res = await apiFetch('/MarketplaceChat/SendMessage', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: String(user.userId),
                    conversationId: numericId,
                    messageText,
                }),
            });
            if (!res.ok) throw new Error('Send failed');
            await fetchMessages(true);
        } catch (error) {
            console.error(error);
            setMessages((prev) => prev.map((message) => (
                message.messageId === tempId
                    ? { ...message, pending: false, failed: true }
                    : message
            )));
        } finally {
            setSending(false);
        }
    };

    const handleSubmitReview = async ({ rating, reviewText }) => {
        const reviewedUserId = Number(conversationField(conversation, 'otherUserId', 'OtherUserId'));
        const marketplaceItemId = Number(conversationField(conversation, 'marketplaceItemId', 'MarketplaceItemId')) || 0;
        if (!reviewedUserId || !numericId) return;
        setSubmittingReview(true);
        try {
            const isEditing = Boolean(existingReview);
            const endpoint = isEditing
                ? '/MarketplaceReviews/UpdateReview'
                : '/MarketplaceReviews/SubmitReview';
            const payload = {
                userId: String(user.userId),
                reviewedUserId,
                rating,
                reviewText,
                conversationId: numericId,
            };

            if (!isEditing) {
                payload.reviewerRole = 'Buyer';
                if (marketplaceItemId > 0) {
                    payload.marketplaceItemId = marketplaceItemId;
                }
            }

            const res = await apiFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Review save failed');

            const saved = await fetchExistingReview(reviewedUserId);
            if (!saved) {
                setExistingReview({
                    reviewId: existingReview?.reviewId ?? Date.now(),
                    rating,
                    reviewText,
                });
            }
            setShowReview(false);
        } catch (error) {
            console.error(error);
        } finally {
            setSubmittingReview(false);
        }
    };

    const reviewedUserId = Number(conversationField(conversation, 'otherUserId', 'OtherUserId'));

    // Any 1:1 chat can leave a rating (API keys reviews to conversation + other user).
    const canReview = !isDraft && reviewedUserId > 0 && numericId > 0;

    return (
        <div className={`conversation-thread conversation-thread-${variant}`}>
            <div className="conversation-thread-header">
                {onBack && (
                    <button type="button" className="back-link" onClick={onBack}>
                        ← Back to Messages
                    </button>
                )}
                <div className="conversation-thread-title-row">
                    <h2>{conversationField(conversation, 'otherUsername', 'OtherUsername') || 'Conversation'}</h2>
                    {canReview && (
                        <button type="button" className="review-btn" onClick={() => setShowReview(true)}>
                            {existingReview ? 'Edit review' : 'Leave a rating'}
                        </button>
                    )}
                </div>
            </div>
            <div className="messages-thread">
                {loadingMessages ? (
                    <p className="messages-status messages-thread-loading">Loading messages…</p>
                ) : messages.length === 0 ? (
                    <p className="messages-status">No messages yet - say hello.</p>
                ) : (
                    <>
                        {buildThreadItems(messages).map((item) => (
                            item.type === 'date' ? (
                                <div key={item.key} className="message-date-separator">
                                    <span>{item.label}</span>
                                </div>
                            ) : (
                                <div
                                    key={item.key}
                                    className={[
                                        'message-bubble',
                                        item.message.senderUserId === user.userId ? 'mine' : 'theirs',
                                        item.message.pending ? 'pending' : '',
                                        item.message.failed ? 'failed' : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <div className="message-text">{renderMessageText(item.message.messageText)}</div>
                                    <div className="message-time">
                                        {item.message.failed
                                            ? 'Failed to send'
                                            : item.message.pending
                                                ? 'Sending…'
                                                : formatChatTime(item.message.sentUnix)}
                                    </div>
                                </div>
                            )
                        ))}
                        <div ref={bottomRef} />
                    </>
                )}
            </div>
            <ReviewDialog
                open={canReview && showReview}
                sellerName={conversationField(conversation, 'otherUsername', 'OtherUsername')}
                itemTitle={conversationField(conversation, 'itemTitle', 'ItemTitle')}
                busy={submittingReview}
                isEditing={Boolean(existingReview)}
                initialRating={existingReview?.rating ?? 5}
                initialReviewText={existingReview?.reviewText ?? ''}
                onConfirm={handleSubmitReview}
                onCancel={() => !submittingReview && setShowReview(false)}
            />
            <form className="message-compose" onSubmit={handleSend}>
                <input
                    type="text"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    placeholder="Type a message..."
                />
                <button type="submit" disabled={sending || !text.trim()}>Send</button>
            </form>
        </div>
    );
};

export default ConversationThread;
